// FUNCTIONALITY-101 / CROSSSELL-109 — Store V2 fixed 17 business tiles (UA + EN).
// Order and labels are the launch contract; counts come from catalog meta.

export interface StoreV2Tile {
  id: string;
  icon: string;
  name: string;
  nameEn: string;
  label: string;
  sort: number;
}

const TILES_RAW: Array<Omit<StoreV2Tile, "label">> = [
  { id: "biz_ramen_buldak", icon: "🍜", name: "Рамени, бульдак", nameEn: "Ramen, Buldak", sort: 1 },
  { id: "biz_snacks", icon: "🍘", name: "Снеки", nameEn: "Snacks", sort: 2 },
  { id: "biz_asian_tea", icon: "🍵", name: "Азійський чай, пуери", nameEn: "Asian tea, pu-erh", sort: 3 },
  { id: "biz_sauces", icon: "🥢", name: "Соуси", nameEn: "Sauces", sort: 4 },
  { id: "biz_drinks", icon: "🥤", name: "Напої", nameEn: "Drinks", sort: 5 },
  { id: "biz_desserts", icon: "🍡", name: "Десерти, цукор, мальтоза", nameEn: "Desserts, sugar, maltose", sort: 6 },
  {
    id: "biz_fruit_concentrates",
    icon: "🍋",
    name: "Фруктові концентрати, основи для чаїв та лимонадів",
    nameEn: "Fruit concentrates, tea & lemonade bases",
    sort: 7,
  },
  { id: "biz_pastes_spices", icon: "🌶️", name: "Пасти і спеції", nameEn: "Pastes & spices", sort: 8 },
  { id: "biz_rice_noodles", icon: "🍚", name: "Рис, локшина, борошно", nameEn: "Rice, noodles, flour", sort: 9 },
  { id: "biz_spring_rolls", icon: "🥟", name: "Спринг-роли, тісто, вонтони", nameEn: "Spring rolls, dough, wontons", sort: 10 },
  { id: "biz_veg_fruit_preserve", icon: "🥭", name: "Овочі, фрукти, консервація", nameEn: "Veg, fruit, preserves", sort: 11 },
  { id: "biz_cooking_sets", icon: "🍱", name: "Набори для приготування", nameEn: "Cooking sets", sort: 12 },
  { id: "biz_seaweed", icon: "🌊", name: "Водорості, норі, вакаме", nameEn: "Seaweed, nori, wakame", sort: 13 },
  { id: "biz_coconut_milk", icon: "🥥", name: "Кокосове молоко", nameEn: "Coconut milk", sort: 14 },
  { id: "biz_caviar_seafood", icon: "🍣", name: "Ікра, морепродукти", nameEn: "Caviar & seafood", sort: 15 },
  { id: "biz_diet", icon: "🥗", name: "Дієтичні продукти", nameEn: "Diet products", sort: 16 },
  { id: "biz_equipment", icon: "🍳", name: "Обладнання для азіатської кухні", nameEn: "Asian kitchen equipment", sort: 17 },
];

export const STORE_V2_TILES: StoreV2Tile[] = TILES_RAW.map((t) => ({
  ...t,
  label: `${t.icon} ${t.name}`,
}));

/** R1G10F6 — merge Manager-created mgr_cat_* tiles from categories API onto baseline. */
export function mergeStoreTilesFromApi(
  categories: Array<{ id?: string; label?: string; name?: string; emoji?: string | null; sort?: number }> | null | undefined,
): StoreV2Tile[] {
  const base = [...STORE_V2_TILES];
  const seen = new Set(base.map((t) => t.id));
  let sort = 100;
  for (const c of categories ?? []) {
    const id = String(c.id ?? "").trim();
    if (!id.startsWith("mgr_cat_") || seen.has(id)) continue;
    seen.add(id);
    const rawLabel = String(c.label ?? c.name ?? id).trim();
    const emojiMatch = rawLabel.match(/^(\p{Extended_Pictographic}\uFE0F?)\s*/u);
    const icon = String(c.emoji ?? emojiMatch?.[1] ?? "📦");
    const name = rawLabel.replace(/^(\p{Extended_Pictographic}\uFE0F?)\s*/u, "").trim() || id;
    base.push({
      id,
      icon,
      name,
      nameEn: name,
      label: `${icon} ${name}`,
      sort: Number(c.sort) || sort++,
    });
  }
  return base;
}

export function tileById(id: string): StoreV2Tile | null {
  return STORE_V2_TILES.find((t) => t.id === id) ?? null;
}

export function tileLabel(id: string, lang: "ua" | "en" = "ua"): string {
  const tile = tileById(id);
  if (!tile) return id;
  const name = lang === "en" ? tile.nameEn : tile.name;
  return `${tile.icon} ${name}`;
}

export function tileName(id: string, lang: "ua" | "en" = "ua"): string {
  const tile = tileById(id);
  if (!tile) return id;
  return lang === "en" ? tile.nameEn : tile.name;
}
